/**
 * V2-only presentational pieces for Income / Rent Verification report UI.
 * Portable with the rest of `verification-report-v2/` — no dependency on the
 * frozen V1 `dialogs/verification-bits.tsx`.
 */
import {
	Check,
	ChevronDown,
	CircleUser,
	CreditCard,
	Download,
	Landmark,
	X,
} from 'lucide-react'
import type { ReactNode } from 'react'
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '#app/components/ui/dialog'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '#app/components/ui/tooltip'
import type { VerificationDisclaimer } from '#app/utils/application-detail-v2/verification-disclaimers'
import {
	type AiDeclaredIncomePeriod,
	type ConnectedFlinksAccount,
	INCOME_CATEGORY_COLORS,
	INCOME_CATEGORY_PILL_STYLES,
	INCOME_FILTER_LABELS,
	type IncomeCategoryKey,
	RENT_FILTER_COLORS,
	RENT_FILTER_LABELS,
	RENT_MATCH_PILL_STYLES_V2,
	type RentFilterKey,
	type RentMonthCell,
	formatConnectedDate,
	formatTxnDate,
	formatRentTxnDate,
	formatTxnAmount,
	formatTxnDay,
	formatWholeAmount,
	orderedIncomeCategories,
	orderedRentFilters,
	rentMatchKind,
	visibleRentMonthCells,
	formatRentPeriod,
	type RentPeriod,
} from '#app/utils/application-detail-v2/verification-display'
import { cn } from '#app/utils/misc'
import type { VerificationDataItem } from '#app/utils/report-data-helpers'

/** Income & Rent V2 shell — white modal; gray strip is owned by filters. */
export function VerificationRedesignShell({
	open,
	onOpenChange,
	title,
	accountHolder,
	showRequestBank,
	onRequestBank,
	canDownload,
	onDownload,
	downloadBusy,
	filters,
	children,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	title: string
	accountHolder: string
	showRequestBank: boolean
	onRequestBank: () => void
	canDownload: boolean
	onDownload: () => void
	downloadBusy: boolean
	/** Filter strip rendered full-bleed under the white header. */
	filters?: ReactNode
	children: ReactNode
}) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				showCloseButton={false}
				className="block max-h-[88vh] w-full gap-0 overflow-y-auto rounded-3xl border-[#ECECEA] bg-white p-0 sm:max-w-7xl"
			>
				<DialogHeader className="w-full flex-row px-3 md:px-5 pt-5 pb-4 sm:px-7 sm:pt-6 2xl:px-10 2xl:pt-8">
					<div className="w-full flex flax-wrap flex-col md:flex-row items-start md:items-center justify-between gap-3 space-y-0">
						<DialogTitle className="font-calgary text-[28px] leading-tight font-medium text-[#1F1E1D] 2xl:text-[40px]">
							{title}
						</DialogTitle>
						<DialogDescription className="sr-only">{title}</DialogDescription>
						<div className="mr-[-25px] flex flex-wrap items-center justify-end gap-1 sm:mr-0 sm:gap-2 md:gap-3">
							{canDownload ? (
								<button
									type="button"
									onClick={onDownload}
									disabled={downloadBusy}
									aria-label={
										downloadBusy ? 'Preparing download' : 'Download report'
									}
									className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full border border-[#32302F] bg-white text-[#32302F] transition-colors hover:bg-[#F0F0EE] disabled:cursor-not-allowed disabled:opacity-60 sm:h-8 sm:w-8 md:h-9 md:w-9 2xl:h-10 2xl:w-10"
								>
									<Download className="size-3 sm:size-3.5 md:size-4" />
								</button>
							) : null}
							{showRequestBank ? (
								<button
									type="button"
									onClick={onRequestBank}
									className="flex h-7 cursor-pointer items-center gap-1.5 rounded-full bg-[#32302F] px-2.5 text-[10px] font-medium text-white transition-colors hover:bg-[#1a1918] sm:h-8 sm:gap-2 sm:px-3 sm:text-[12px] md:h-9 md:px-3.5 md:text-[13px] 2xl:h-10 2xl:px-4 2xl:text-[14px]"
								>
									<CreditCard className="size-3 sm:size-3.5 md:size-4" />
									Request Bank Account
								</button>
							) : null}
							{accountHolder ? (
								<div className="min-w-0 max-w-[140px] text-right sm:max-w-[180px] md:max-w-[220px] 2xl:max-w-[280px]">
									<p className="text-[13px] leading-tight text-[#747474] sm:text-[14px] md:text-[15px] 2xl:text-[17px]">
										Account Holder
									</p>
									<p className="truncate text-[14px] font-medium text-[#32302F] sm:text-[16px] md:text-[18px] 2xl:text-[20px]">
										{accountHolder}
									</p>
								</div>
							) : null}
						</div>
					</div>
					<DialogClose
						aria-label="Close"
						className="flex size-8 cursor-pointer items-center justify-center rounded-full text-[#000000] transition-colors hover:bg-[#EFEFED] hover:text-[#32302F]"
					>
						<X className="size-4" />
					</DialogClose>
				</DialogHeader>
				{filters}
				<div className="px-3 md:px-5 pt-3 md:pt-5 pb-6 sm:px-7 sm:pb-7">
					{children}
				</div>
			</DialogContent>
		</Dialog>
	)
}

/** @deprecated Use VerificationRedesignShell — kept as alias for income modal. */
export const IncomeModalShell = VerificationRedesignShell

function CategoryFilterButton({
	active,
	label,
	dotColor,
	onClick,
}: {
	active: boolean
	label: string
	dotColor?: string
	onClick: () => void
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={cn(
				'flex h-7 md:h-9 cursor-pointer items-center gap-1 md:gap-2 rounded-full border px-2 md:px-3.5 text-[11px] md:text-[13px] transition-colors 2xl:h-11.5 2xl:gap-3 2xl:text-[16px]',
				active
					? 'border-[#32302F] bg-white text-[#32302F]'
					: 'border-[#D6D6D4] bg-transparent text-[#747474] hover:border-[#C9C9C7]',
			)}
		>
			{dotColor ? (
				<span
					className="size-1.5 md:size-2 shrink-0 rounded-full"
					style={{ backgroundColor: dotColor }}
					aria-hidden="true"
				/>
			) : null}
			{label}
			<span className="w-4 md:w-5.5">
				<span
					className={cn(
						'flex items-center justify-center rounded-full',
						active
							? 'size-3 md:size-4 bg-[#32302F] text-white'
							: 'size-3.5 md:size-5.5 border border-[#C9C9C7] bg-transparent',
					)}
				>
					{active ? (
						<Check className="size-2 md:size-2.5" strokeWidth={3} />
					) : null}
				</span>
			</span>
		</button>
	)
}

export function IncomeCategoryFilters({
	present,
	selected,
	onChange,
}: {
	present: IncomeCategoryKey[]
	selected: IncomeCategoryKey[]
	onChange: (next: IncomeCategoryKey[]) => void
}) {
	if (present.length === 0) return null
	const allSelected =
		present.length > 0 && present.every((key) => selected.includes(key))

	const toggle = (key: IncomeCategoryKey) => {
		if (selected.includes(key)) {
			onChange(orderedIncomeCategories(selected.filter((k) => k !== key)))
		} else {
			onChange(orderedIncomeCategories([...selected, key]))
		}
	}

	return (
		<div className="flex flex-wrap items-center gap-2 bg-[#F4F6F6] px-5 py-3 sm:px-7 2xl:mt-3">
			<CategoryFilterButton
				active={allSelected}
				label="All"
				dotColor={undefined}
				onClick={() =>
					onChange(allSelected ? [] : orderedIncomeCategories(present))
				}
			/>
			{present.map((key) => {
				const active = selected.includes(key)
				return (
					<CategoryFilterButton
						key={key}
						active={active}
						label={INCOME_FILTER_LABELS[key]}
						dotColor={INCOME_CATEGORY_COLORS[key]}
						onClick={() => toggle(key)}
					/>
				)
			})}
		</div>
	)
}

export function RentCategoryFilters({
	present,
	selected,
	onChange,
}: {
	present: RentFilterKey[]
	selected: RentFilterKey[]
	onChange: (next: RentFilterKey[]) => void
}) {
	if (present.length === 0) return null
	const allSelected =
		present.length > 0 && present.every((key) => selected.includes(key))

	const toggle = (key: RentFilterKey) => {
		if (selected.includes(key)) {
			onChange(orderedRentFilters(selected.filter((k) => k !== key)))
		} else {
			onChange(orderedRentFilters([...selected, key]))
		}
	}

	return (
		<div className="flex flex-wrap items-center gap-2 bg-[#F4F6F6] px-5 py-3 sm:px-7 2xl:mt-8">
			<CategoryFilterButton
				active={allSelected}
				label="All"
				dotColor={undefined}
				onClick={() => onChange(allSelected ? [] : orderedRentFilters(present))}
			/>
			{present.map((key) => {
				const active = selected.includes(key)
				return (
					<CategoryFilterButton
						key={key}
						active={active}
						label={RENT_FILTER_LABELS[key]}
						dotColor={RENT_FILTER_COLORS[key]}
						onClick={() => toggle(key)}
					/>
				)
			})}
		</div>
	)
}

export function RentMatchPill({
	category,
}: {
	category: string | null | undefined
}) {
	const kind = rentMatchKind(category)
	const style = kind ? RENT_MATCH_PILL_STYLES_V2[kind] : null
	if (!style) {
		return (
			<span className="inline-flex shrink-0 items-center rounded-full bg-[#F4F6F6] px-2.5 py-0.5 text-[11px] font-medium text-[#747474] 2xl:text-[14px]">
				{category?.replace(/_/g, ' ') || '-'}
			</span>
		)
	}
	return (
		<span
			className="inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium 2xl:text-[14px]"
			style={{ backgroundColor: style.bg, color: style.text }}
		>
			{style.label}
		</span>
	)
}

export function RentSummarySection({
	declaredRent,
	previousDeclaredRent,
	months,
	currentPeriod,
	previousPeriod,
}: {
	declaredRent: number | null
	previousDeclaredRent?: number | null
	months: RentMonthCell[]
	currentPeriod?: RentPeriod
	previousPeriod?: RentPeriod
}) {
	const visibleMonths = visibleRentMonthCells(months)
	const getMonthStyles = (status: RentMonthCell['status']) => {
		switch (status) {
			case 'strong':
				return {
					border: '#55C98B',
					text: '#3FBB78',
					iconBg: '#EFFBF4',
					icon: <Check className="size-6" strokeWidth={2} />,
				}

			case 'possible':
				return {
					border: '#F5B642',
					text: '#F5A400',
					iconBg: '#FFF9ED',
					icon: <span className="text-[24px] font-medium">!</span>,
				}

			case 'none':
				return {
					border: '#FF6872',
					text: '#FF5B66',
					iconBg: '#FFF2F3',
					icon: <X className="size-7" strokeWidth={2} />,
				}

			case 'empty':
			default:
				return {
					border: '#C9C9C7',
					text: '#747474',
					iconBg: '#F0F0EE',
					icon: <span className="text-lg">-</span>,
				}
		}
	}
	return (
		<section className="rounded-2xl border border-[#EFEFED] bg-white p-4 sm:p-6 2xl:p-7">
			<h3 className="text-[16px] font-semibold text-[#32302F] 2xl:text-[18px]">
				Declared Rent
			</h3>
			<div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:gap-9">
				<DeclaredRentChip
					label="Current Declared Rent"
					amount={declaredRent}
					tone="purple"
					period={currentPeriod}
				/>
				{previousDeclaredRent != null ? (
					<DeclaredRentChip
						label="Previous Declared Rent"
						amount={previousDeclaredRent}
						tone="neutral"
						period={previousPeriod}
					/>
				) : null}
			</div>
			<div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-[#747474] 2xl:text-[14px]">
				<span className="flex items-center gap-2">
					<span
						className="size-2 rounded-full"
						style={{ backgroundColor: '#3FBB78' }}
					/>
					Strong Match
				</span>
				<span className="flex items-center gap-2">
					<span
						className="size-2 rounded-full"
						style={{ backgroundColor: '#F5A400' }}
					/>
					Possible Match
				</span>
				<span className="flex items-center gap-2">
					<span
						className="size-2 rounded-full"
						style={{ backgroundColor: '#FF6872' }}
					/>
					No Payment Found
				</span>
			</div>

			{visibleMonths.length > 0 ? (
				<TooltipProvider>
					<div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-6 2xl:mt-5">
						{visibleMonths.map((month) => {
							const styles = getMonthStyles(month.status)
							const items = month.items ?? []
							const showTooltip = items.length > 0 || month.status === 'none'

							const cell = (
								<div
									key={month.key}
									className="flex min-w-0 flex-col items-center justify-center gap-3 rounded-xl border bg-white px-1 py-4 text-center 2xl:py-5"
									style={{
										borderColor: styles.border,
										color: styles.text,
									}}
								>
									<span className="w-full text-center text-[12px] font-semibold leading-none whitespace-nowrap 2xl:text-[15px]">
										{month.shortLabel}
									</span>
									<span
										className="flex size-11 items-center justify-center rounded-full 2xl:size-12"
										style={{ backgroundColor: styles.iconBg }}
									>
										{styles.icon}
									</span>
								</div>
							)

							if (!showTooltip) return <div key={month.key}>{cell}</div>

							return (
								<Tooltip key={month.key} delayDuration={200}>
									<TooltipTrigger asChild>
										<div className="cursor-default">{cell}</div>
									</TooltipTrigger>
									<TooltipContent side="top" className="rounded-2xl p-4 shadow-xl shadow-black/5 bg-white border border-[#EFEFED] max-w-[280px]">
										<div className="flex flex-col gap-1 font-sans">
											<div className="flex items-center justify-between gap-4">
												<span className="text-base font-semibold text-[#32302F]">
													{month.label}
												</span>
												{month.status === 'strong' || month.status === 'possible' ? (
													<span
														className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
														style={{
															backgroundColor: RENT_MATCH_PILL_STYLES_V2[month.status].bg,
															color: RENT_MATCH_PILL_STYLES_V2[month.status].text,
														}}
													>
														{RENT_MATCH_PILL_STYLES_V2[month.status].label}
													</span>
												) : null}
											</div>

											{items.length > 0 ? (
												<>
													<div className="text-[13px] text-[#747474]">Payment Dates</div>

													<div className="flex flex-col gap-1.5">
														{items.map((item, idx) => (
															<div key={idx} className="flex items-center justify-between text-sm text-[#32302F]">
																<span>{formatTxnDate(item.date)}</span>
																<span>{formatTxnAmount(item.amount)}</span>
															</div>
														))}
													</div>

													<div className="h-px w-full bg-[#EFEFED]" />
													<div className="flex items-center justify-between gap-4 text-sm font-medium text-[#32302F]">
														<span>Total</span>
														<span>{formatTxnAmount(month.amount)}</span>
													</div>
												</>
											) : (
												<div className="text-[13px] text-[#747474]">No payments found</div>
											)}
										</div>
									</TooltipContent>
								</Tooltip>
							)
						})}
					</div>
				</TooltipProvider>
			) : null}
		</section>
	)
}
function DeclaredRentChip({
	label,
	amount,
	tone = 'neutral',
	period,
}: {
	label: string
	amount: number | null
	tone?: 'neutral' | 'purple'
	period?: RentPeriod
}) {
	const isPurple = tone === 'purple'
	const periodLabel = formatRentPeriod(period)

	return (
		<div
			className={`flex flex-col gap-3 rounded-2xl border px-3.5 py-3 2xl:gap-4 2xl:px-5 2xl:py-4 ${isPurple
				? 'border-[#DCCCEA] bg-[#DCCCEA66]'
				: 'border-[#00000005] bg-[#00000005]'
				}`}
		>
			<div className="flex items-center gap-3">
				<span
					className={`flex size-9 shrink-0 items-center justify-center rounded-[12px] 2xl:size-11 ${isPurple
						? 'bg-[#FBFDFD] text-[#9976B8]'
						: 'bg-[#FBFDFD] text-[#747474]'
						}`}
				>
					<CircleUser className="size-5 2xl:size-6" strokeWidth={1.75} />
				</span>

				<div className="min-w-0">
					<p
						className={`text-[12px] 2xl:text-[16px] ${isPurple ? 'text-[#32302F]' : 'text-[#747474]'
							}`}
					>
						{label}
					</p>

					<p className="text-[16px] font-semibold leading-tight whitespace-nowrap text-[#32302F] 2xl:text-[22px]">
						{formatWholeAmount(amount)}

						<span className="ml-1 text-[10px] font-normal text-[#747474] 2xl:text-[13px]">
							/month
						</span>
					</p>
				</div>
			</div>

			<p className="flex items-center gap-1 border-t border-black/5 pt-2 text-[11px] text-[#747474] 2xl:text-[14px]">
				{periodLabel ? (
					<>
						{periodLabel.range && <span>{periodLabel.range} &middot;</span>}
						<span className="text-[#32302F]">{periodLabel.duration}</span>
					</>
				) : (
					'-'
				)}
			</p>
		</div>
	)
}
export function RentTransactionTable({
	items,
	totalCount,
}: {
	items: VerificationDataItem[]
	/** Unfiltered count, for "13 Of 24 Transactions". Defaults to `items`. */
	totalCount?: number
}) {
	const total = totalCount ?? items.length
	if (items.length === 0) {
		return (
			<p className="mt-4 text-sm text-[#747474]">
				No rent-related transactions were identified for this applicant.
			</p>
		)
	}
	return (
		<section className="mt-5 overflow-hidden rounded-2xl border border-[#EFEFED] bg-white">
			<div className="flex items-center justify-between gap-3 px-4 py-3 2xl:px-5">
				<h3 className="text-[15px] font-semibold text-[#32302F] 2xl:text-[18px]">
					Rent Transactions
				</h3>
				<span className="shrink-0 text-[12px] text-[#747474] 2xl:text-[14px]">
					{items.length} Of {total} Transactions
				</span>
			</div>
			<div className="overflow-x-auto">
				<table className="w-full min-w-[640px] border-collapse text-left">
					<thead>
						<tr className="bg-[#F4F6F6] text-[13px] font-medium text-[#32302F] 2xl:text-[15px]">
							<th className="w-0 px-4 py-2.5 font-medium whitespace-nowrap 2xl:px-5 2xl:py-3">
								Date
							</th>
							<th className="w-full px-4 py-2.5 font-medium 2xl:px-5 2xl:py-3">
								Description
							</th>
							<th className="w-0 px-6 py-2.5 text-center font-medium whitespace-nowrap 2xl:px-5 2xl:py-3">
								Match
							</th>
							<th className="w-0 px-4 py-2.5 text-right font-medium whitespace-nowrap 2xl:px-5 2xl:py-3">
								Amount
							</th>
						</tr>
					</thead>
					<tbody>
						{items.map((item, index) => (
							<tr
								key={`${item.date}-${index}`}
								className={cn(
									'text-[12px] text-[#32302F] 2xl:text-[14px]',
									index > 0 && 'border-t border-dashed border-[#E8E8E6]',
								)}
							>
								<td className="w-0 px-4 py-3 whitespace-nowrap text-[#32302F] 2xl:px-5 2xl:py-3.5">
									{formatRentTxnDate(item.date)}
								</td>
								<td className="w-full min-w-0 px-4 py-3 text-[#747474] 2xl:px-5 2xl:py-3.5">
									{item.description || '-'}
								</td>
								<td className="w-0 px-6 py-3 whitespace-nowrap 2xl:px-5 2xl:py-3.5">
									<RentMatchPill category={item.category} />
								</td>
								<td className="w-0 px-4 py-3 text-right font-semibold whitespace-nowrap text-[#32302F] tabular-nums 2xl:px-5 2xl:py-3.5">
									{formatTxnAmount(item.amount)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</section>
	)
}

function DeclaredAmountCard({
	caption,
	amount,
	tone,
}: {
	caption: string
	amount: number | null
	tone: 'neutral' | 'purple'
}) {
	const purple = tone === 'purple'
	return (
		<div
			className={cn(
				'flex min-w-0 flex-1 items-center gap-3 rounded-2xl border px-4 py-3 2xl:gap-4 2xl:px-5 2xl:py-4',
				purple
					? 'border-[#DCCCEA] bg-[#DCCCEA66]'
					: 'border-[#00000005] bg-[#00000005]',
			)}
		>
			<span
				className={cn(
					'flex size-10 shrink-0 items-center justify-center rounded-[12px] 2xl:size-12',
					purple
						? 'bg-[#FBFDFD] border-[#F0F0F0] text-[#9976B8]'
						: 'bg-[#FBFDFD] border-[#F0F0F0] text-[#747474]',
				)}
			>
				<CircleUser className="size-5 2xl:size-6" strokeWidth={1.75} />
			</span>
			<div className="min-w-0">
				<p
					className={cn(
						'text-[12px] 2xl:text-[16px]',
						purple ? 'text-[#32302F]' : 'text-[#747474]',
					)}
				>
					{caption}
				</p>
				<p className="text-[18px] leading-tight font-medium whitespace-nowrap text-[#32302F] 2xl:text-[22px]">
					{formatWholeAmount(amount)}
					<span className="ml-1 text-[13px] font-normal text-[#747474] 2xl:text-[14px]">
						/month
					</span>
				</p>
			</div>
		</div>
	)
}

function DeclaredPeriodCards({
	showLabel,
	label,
	period,
	timelinePeriod,
}: {
	showLabel: boolean
	label: string
	period: AiDeclaredIncomePeriod | null
	timelinePeriod?: RentPeriod
}) {
	const timelineParts = formatRentPeriod(timelinePeriod)

	return (
		<div className="flex min-w-0 items-center gap-3">
			{showLabel ? (
				<div className="w-[140px] shrink-0">
					<p className="text-[12px] font-medium text-[#32302F] 2xl:text-[16px]">
						{label}
					</p>
					{timelineParts ? (
						<p className="mt-0.5 flex items-center text-[10px] leading-tight text-[#747474] whitespace-nowrap 2xl:text-[13px]">
							{timelineParts.range && <span>{timelineParts.range}</span>}
						</p>
					) : null}
				</div>
			) : null}

			<div className="flex min-w-0 flex-1 gap-[10px]">
				<div className="min-w-0 flex-1">
					<DeclaredAmountCard
						caption="Gross"
						amount={period?.grossMonthly ?? null}
						tone="neutral"
					/>
				</div>

				<div className="min-w-0 flex-1">
					<DeclaredAmountCard
						caption="Net"
						amount={period?.netMonthly ?? null}
						tone="purple"
					/>
				</div>
			</div>
		</div>
	)
}

function declaredNetDeltaLabel(
	averageMonthly: number | null,
	declaredNet: number | null,
): { text: string; tone: 'below' | 'above' | 'match' } | null {
	if (averageMonthly == null || declaredNet == null) return null
	const delta = Math.round(averageMonthly) - Math.round(declaredNet)
	const abs = Math.abs(delta).toLocaleString('en-CA', {
		maximumFractionDigits: 0,
	})
	if (delta < 0) {
		return {
			text: `$${abs}/Month Below Declared Net`,
			tone: 'below',
		}
	}
	if (delta > 0) {
		return {
			text: `$${abs}/Month Above Declared Net`,
			tone: 'above',
		}
	}
	return { text: 'Matches Declared Net', tone: 'match' }
}

export function IncomeComparisonSummary({
	current,
	previous,
	currentTimeline,
	previousTimeline,
	showPrevious,
	salaryCaption,
	averageMonthly,
}: {
	current: AiDeclaredIncomePeriod | null
	previous: AiDeclaredIncomePeriod | null
	currentTimeline?: RentPeriod
	previousTimeline?: RentPeriod
	/** When true, always render the Previous row (null → '-'). */
	showPrevious: boolean
	salaryCaption: string | null
	averageMonthly: number | null
}) {
	const delta = declaredNetDeltaLabel(
		averageMonthly,
		current?.netMonthly ?? null,
	)

	return (
		<div className="grid min-h-[212px] grid-cols-[minmax(0,2.0358fr)_minmax(0,1fr)] rounded-2xl border border-[#EFEFED] bg-white">
			{/* Left: What They Declared */}
			<div className="min-w-0 p-[30px]">
				<p className="text-[14px] font-medium text-[#32302F] 2xl:text-[20px]">
					What They Declared
				</p>

				{salaryCaption ? (
					<p className="mt-1 text-[12px] text-[#747474] 2xl:text-[16px]">
						{salaryCaption}
					</p>
				) : null}
				<div className="mt-3 flex flex-col gap-[10px]">
					<DeclaredPeriodCards
						showLabel={showPrevious}
						label="Current"
						period={current}
						timelinePeriod={currentTimeline}
					/>
					{showPrevious ? (
						<DeclaredPeriodCards
							showLabel={true}
							label="Previous"
							period={previous}
							timelinePeriod={previousTimeline}
						/>
					) : null}
				</div>
			</div>

			{/* Right: Actually In Their Account */}
			<div className="flex min-w-0 flex-col items-center justify-center border-l border-[#EFEFED] p-[30px] text-center">
				<p className="text-[14px] font-medium text-[#32302F] 2xl:text-[20px]">
					Actually In Their Account
				</p>
				<p className="mt-3 text-[32px] font-medium leading-tight tracking-tight text-[#32302F] 2xl:text-[40px]">
					{formatWholeAmount(averageMonthly)}
					<span className="ml-1 text-[15px] font-normal text-[#747474] 2xl:text-[18px]">
						/month
					</span>
				</p>

				<p className="mt-1 text-[13px] text-[#747474] 2xl:text-[16px]">
					Average Monthly Deposits
				</p>

				{delta ? (
					<span
						className={cn(
							'mt-2 inline-flex rounded-full px-3 py-1 text-[12px] font-medium 2xl:text-[14px]',
							delta.tone === 'below' &&
							'bg-[#F6ABCB1A] text-[#DC2634]',
							delta.tone === 'above' &&
							'bg-[#EAF7EE] text-[#2F8F4E]',
							delta.tone === 'match' &&
							'bg-[#F4F6F6] text-[#747474]',
						)}
					>
						{delta.text}
					</span>
				) : null}
			</div>
		</div>
	)
}

export function IncomeCategoryPill({
	category,
}: {
	category: string | null | undefined
}) {
	const key = category as IncomeCategoryKey | undefined
	const known = key && key in INCOME_FILTER_LABELS
	const label = known
		? INCOME_FILTER_LABELS[key as IncomeCategoryKey]
		: category?.replace(/_/g, ' ') || 'Other'
	const style = known
		? INCOME_CATEGORY_PILL_STYLES[key as IncomeCategoryKey]
		: { bg: '#F4F6F6', text: '#747474' }
	return (
		<span
			className="inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium whitespace-nowrap 2xl:text-[14px]"
			style={{ backgroundColor: style.bg, color: style.text }}
		>
			{label}
		</span>
	)
}

export function MonthByMonthList({
	groups,
}: {
	groups: Array<{
		key: string
		label: string
		depositCount: number
		total: number
		items: VerificationDataItem[]
	}>
}) {
	if (groups.length === 0) {
		return (
			<p className="mt-4 text-sm text-[#747474]">
				No income transactions were identified for this applicant.
			</p>
		)
	}
	return (
		<section className="rounded-2xl border border-[#EFEFED] bg-white p-4 sm:p-5">
			<h3 className="text-[15px] font-medium text-[#32302F] 2xl:text-[20px]">
				Month By Month
			</h3>
			<div className="mt-4 space-y-3">
				{groups.map((group) => (
					<div
						key={group.key}
						className="overflow-hidden rounded-xl border border-[#EFEFED] bg-white"
					>
						<div className="flex items-start justify-between gap-3 bg-[#F4F6F6] px-4 py-3 2xl:px-4 2xl:py-3">
							<div className="min-w-0">
								<p className="text-[14px] font-semibold text-[#32302F] 2xl:text-[18px]">
									{group.label}
								</p>
								<p className="text-[12px] text-[#747474] 2xl:text-[14px]">
									{group.depositCount}{' '}
									{group.depositCount === 1 ? 'Deposit' : 'Deposits'}
								</p>
							</div>
							<div className="shrink-0 text-right">
								<p className="text-[14px] font-semibold tabular-nums text-[#32302F] 2xl:text-[18px]">
									{formatWholeAmount(group.total)}
								</p>
								<p className="text-[12px] text-[#747474] 2xl:text-[14px]">
									Month Total
								</p>
							</div>
						</div>
						<div className="overflow-x-auto">
							{/* table-fixed + shared col widths: each month is its own
							    table, so auto layout let longer type pills (e.g.
							    Government Benefits) widen Type and shift Date.
							    Inline col widths — Tailwind utilities on <col> are
							    unreliable; extra Type→Date padding restores the gap. */}
							<table className="w-full min-w-[720px] table-fixed border-collapse text-left">
								<colgroup>
									{/* Sized for longest pill ("Potentially Related To Income") at 2xl. */}
									<col style={{ width: 300 }} />
									<col style={{ width: 180 }} />
									<col />
									<col style={{ width: 104 }} />
								</colgroup>
								<thead>
									<tr className="text-[11px] font-medium tracking-wide text-[#747474] uppercase 2xl:text-[12px]">
										<th className="py-2 pr-8 pl-4 font-medium 2xl:pl-5">
											Type
										</th>
										<th className="px-4 py-2 font-medium whitespace-nowrap">
											Date
										</th>
										<th className="px-4 py-2 font-medium">Description</th>
										<th className="py-2 pr-4 pl-4 text-right font-medium whitespace-nowrap 2xl:pr-5">
											Amount
										</th>
									</tr>
								</thead>
								<tbody>
									{group.items.map((item, index) => (
										<tr
											key={`${item.date}-${index}`}
											className={cn(
												'text-[12px] text-[#32302F] 2xl:text-[14px]',
												index > 0 && 'border-t border-dashed border-[#E8E8E6]',
											)}
										>
											<td className="py-3 pr-8 pl-4 align-middle 2xl:py-3.5 2xl:pl-5">
												<IncomeCategoryPill category={item.category} />
											</td>
											<td className="px-4 py-3 align-middle whitespace-nowrap text-[#32302F] 2xl:py-3.5">
												{formatTxnDay(item.date)}
											</td>
											<td className="min-w-0 truncate px-4 py-3 align-middle text-[#32302F] 2xl:py-3.5">
												{item.description || '-'}
											</td>
											<td className="py-3 pr-4 pl-4 text-right align-middle font-semibold whitespace-nowrap tabular-nums text-[#32302F] 2xl:py-3.5 2xl:pr-5">
												{formatTxnAmount(item.amount)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				))}
			</div>
		</section>
	)
}

export function FinancialInstitutionsList({
	accounts,
}: {
	accounts: ConnectedFlinksAccount[]
}) {
	if (accounts.length === 0) return null
	return (
		<section className="mt-5 rounded-2xl border border-[#EFEFED] bg-white p-4 sm:p-5">
			<h3 className="text-[16px] font-semibold text-[#32302F] 2xl:text-[20px]">
				Financial Institution
			</h3>
			<ul className="mt-3 flex flex-col gap-3">
				{accounts.map((account) => (
					<li
						key={account.loginId}
						className="flex items-center gap-3 rounded-2xl border border-[#EFEFED] bg-white px-4 py-3.5 2xl:px-6 2xl:py-4"
					>
						<div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[#efeaf6] text-[#9976B8]">
							<Landmark className="size-5" strokeWidth={1.75} />
						</div>
						<div className="min-w-0 flex-1">
							<p className="truncate text-[14px] font-semibold text-[#32302F] 2xl:text-[16px]">
								{account.institution || 'Connected bank'}
							</p>
							<p className="mt-0.5 truncate text-[12px] text-[#747474] 2xl:text-[13px]">
								{formatConnectedDate(account.connectedAt)}
							</p>
							{account.accountHolder ? (
								<p className="mt-1 truncate text-[12px] sm:hidden">
									<span className="text-[#747474]">Account Holder: </span>
									<span className="font-semibold text-[#32302F]">
										{account.accountHolder}
									</span>
								</p>
							) : null}
						</div>
						{account.accountHolder ? (
							<p className="hidden max-w-[40%] shrink-0 truncate text-right text-[12px] sm:block 2xl:text-[14px] 2xl:text-[18px]">
								<span className="text-[#747474]">Account Holder: </span>
								<span className="font-semibold text-[#32302F]">
									{account.accountHolder}
								</span>
							</p>
						) : null}
					</li>
				))}
			</ul>
		</section>
	)
}

export function CollapsibleDisclaimers({
	disclaimers,
}: {
	disclaimers: VerificationDisclaimer[]
}) {
	return (
		<details
			open
			className="group mt-5 rounded-2xl border border-[#EFEFED] bg-white"
		>
			<summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4 text-[15px] font-semibold text-[#32302F] 2xl:px-6 2xl:py-5 2xl:text-[20px] [&::-webkit-details-marker]:hidden">
				Disclaimers
				<ChevronDown className="size-4 text-[#747474] transition-transform group-open:rotate-180" />
			</summary>
			<div className="flex flex-col gap-5 px-5 pb-4 2xl:px-6 2xl:pb-5">
				{disclaimers.map((disclaimer) => (
					<div key={disclaimer.heading}>
						<p className="text-[12px] font-semibold text-[#32302F] 2xl:text-[14px]">
							{disclaimer.heading}
						</p>
						<p className="mt-1.5 text-[10px] leading-relaxed text-[#747474] 2xl:text-[13px]">
							{disclaimer.body}
						</p>
					</div>
				))}
			</div>
		</details>
	)
}
